if (!customElements.get('store-locator')) {
  class StoreLocator extends HTMLElement {
    static SELECTORS = {
      data: 'script[data-store-locator-data]',
      search: '[data-store-locator-search]',
      searchButton: '[data-store-locator-search-btn]',
      suggestions: '[data-store-locator-suggestions]',
      suggestion: '[data-suggestion-index]',
      note: '[data-store-locator-note]',
      filter: '[data-store-locator-filter]',
      productSelect: '[data-store-locator-product]',
      locate: '[data-store-locator-locate]',
      locateLabel: '[data-store-locator-locate-label]',
      geoError: '[data-store-locator-error]',
      count: '[data-store-locator-count]',
      sorted: '[data-store-locator-sorted]',
      list: '[data-store-locator-list]',
      empty: '[data-store-locator-empty]',
      detail: '[data-store-locator-detail]',
      map: '[data-store-locator-map]',
      mapCard: '[data-store-locator-map-card]',
      locatedBadge: '[data-store-locator-located]',
      card: '[data-store-id]',
      detailClose: '[data-store-locator-close]'
    };

    static CLASSES = {
      cardSelected: 'store-locator__card--selected',
      suggestionActive: 'store-locator__suggestion--active',
      locateBusy: 'store-locator__locate--busy'
    };

    static MAP = {
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      maxZoom: 19,
      fallbackCenter: [46.6, 2.4],
      fallbackZoom: 5,
      zoomControlPosition: 'bottomright',
      selectZoom: 13,
      fitPadding: [48, 48]
    };

    static PIN = {
      radius: 9,
      selectedRadius: 13,
      hoverRadius: 11,
      color: '#262626',
      hoverColor: '#4a4a4a',
      selectedColor: '#FCED8D',
      stroke: '#ffffff',
      selectedStroke: '#262626',
      weight: 3,
      fillOpacity: 1
    };

    static USER_PIN = { radius: 8, color: '#5DB9BC', stroke: '#ffffff', weight: 3, fillOpacity: 1 };
    static RADIUS_CIRCLE = { color: '#5DB9BC', weight: 2, fillOpacity: 0.08, interactive: false };

    /* French national address base (Géoplateforme) — free, no key, France only */
    static GEOCODER = {
      url: 'https://data.geopf.fr/geocodage/search',
      minChars: 3,
      debounceMs: 250,
      placeLimit: 5,
      cityLimit: 2,
      cityMinScore: 0.5,
      cityType: 'municipality'
    };

    /* A city's radius grows with its population, so "Bordeaux" covers Bordeaux, not a fixed 10 km */
    static RADIUS = { defaultKm: 10, cityMinKm: 3, cityMaxKm: 15, cityPopulationDivisor: 80, minResults: 3 };
    static SUGGEST = { storeLimit: 3 };
    static ORIGIN_SOURCES = { place: 'place', geo: 'geo' };
    static SUGGESTION_KINDS = { store: 'store', place: 'place' };
    static GEO_OPTIONS = { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 };
    static EARTH_RADIUS_KM = 6371;

    connectedCallback() {
      const data = this._parseData();
      if (!data) return;
      this._abort = new AbortController();
      this._strings = data.strings;
      this._stores = this._prepareStores(data.stores);
      this._state = { product: '', selectedId: null, origin: null, locating: false, activeSuggestion: -1 };
      this._suggestions = [];
      this._markers = new Map();
      this._initElements();
      this._initProductFilter();
      this._bindEvents();
      this._initMap();
      this._render();
      this._loadFeed();
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._geocodeAbort?.abort();
      clearTimeout(this._suggestTimer);
      this._resizeObserver?.disconnect();
      this._map?.remove();
      this._map = null;
    }

    /* ── Setup ── */

    _parseData() {
      const script = this.querySelector(StoreLocator.SELECTORS.data);
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error('store-locator: invalid data island', error);
        return null;
      }
    }

    /* Every point of sale, from the Lyra feed set on the section. The stores rendered in
       Liquid (Shopify caps them at 50) stay on screen until it answers, and if it fails. */
    async _loadFeed() {
      const url = this.dataset.feedUrl;
      if (!url) return;
      try {
        const response = await fetch(url, { signal: this._abort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const feed = await response.json();
        if (!Array.isArray(feed.stores) || feed.stores.length === 0) return;
        this._stores = this._prepareStores(feed.stores);
        this._state.selectedId = null;
        this._resetProductFilter();
        this._render();
      } catch (error) {
        if (error.name !== 'AbortError') console.warn('store-locator: feed unavailable, showing the stores in the page', error);
      }
    }

    _prepareStores(rawStores) {
      return (rawStores || [])
        .filter((store) => store.name)
        .map((store, index) => ({
          ...store,
          /* Cards and pins key on a number; the source's own id (a Lyra code) is kept as `code` */
          id: index + 1,
          code: store.id,
          lat: this._toFloat(store.lat, null),
          lng: this._toFloat(store.lng, null),
          type: store.type || '',
          products: Array.isArray(store.products) ? store.products.map((item) => item.trim()).filter(Boolean) : []
        }));
    }

    _initElements() {
      const s = StoreLocator.SELECTORS;
      this._searchInput = this.querySelector(s.search);
      this._searchButton = this.querySelector(s.searchButton);
      this._suggestionsEl = this.querySelector(s.suggestions);
      this._noteEl = this.querySelector(s.note);
      this._filterEl = this.querySelector(s.filter);
      this._productSelect = this.querySelector(s.productSelect);
      this._locateBtn = this.querySelector(s.locate);
      this._locateLabel = this.querySelector(s.locateLabel);
      this._geoError = this.querySelector(s.geoError);
      this._countEl = this.querySelector(s.count);
      this._sortedEl = this.querySelector(s.sorted);
      this._listEl = this.querySelector(s.list);
      this._emptyEl = this.querySelector(s.empty);
      this._detailEl = this.querySelector(s.detail);
      this._mapEl = this.querySelector(s.map);
      this._mapCardEl = this.querySelector(s.mapCard);
      this._locatedBadge = this.querySelector(s.locatedBadge);
    }

    /* Options come from the products listed on the store entries — nothing to configure by hand */
    _initProductFilter() {
      if (!this._productSelect) return;
      const products = this._distinctProducts();
      if (products.length === 0) return;
      this._productSelect.append(...products.map((product) => new Option(product, product)));
      this._filterEl.hidden = false;
    }

    _resetProductFilter() {
      if (!this._productSelect) return;
      [...this._productSelect.options].slice(1).forEach((option) => option.remove());
      this._state.product = '';
      this._initProductFilter();
    }

    _distinctProducts() {
      const names = new Set(this._stores.flatMap((store) => store.products));
      return [...names].sort((a, b) => a.localeCompare(b));
    }

    _bindEvents() {
      const { signal } = this._abort;

      this._searchInput?.addEventListener('input', () => this._onSearchInput(), { signal });
      this._searchInput?.addEventListener('keydown', (event) => this._onSearchKeydown(event), { signal });
      this._searchInput?.addEventListener('blur', () => this._closeSuggestions(), { signal });
      this._searchButton?.addEventListener('click', () => this._submitSearch(), { signal });

      /* Keeps focus in the input so its blur doesn't close the list before the click lands */
      this._suggestionsEl?.addEventListener('mousedown', (event) => event.preventDefault(), { signal });
      this._suggestionsEl?.addEventListener('click', (event) => {
        const option = event.target.closest(StoreLocator.SELECTORS.suggestion);
        if (option) this._pickSuggestion(this._toInt(option.dataset.suggestionIndex, -1));
      }, { signal });

      this._productSelect?.addEventListener('change', () => this._applyProductFilter(), { signal });

      this._locateBtn?.addEventListener('click', () => this._locate(), { signal });

      this._listEl?.addEventListener('click', (event) => {
        const card = event.target.closest(StoreLocator.SELECTORS.card);
        if (card) this._select(this._toInt(card.dataset.storeId, 0));
      }, { signal });

      this._listEl?.addEventListener('mouseover', (event) => {
        const card = event.target.closest(StoreLocator.SELECTORS.card);
        if (card) this._setHoveredPin(this._toInt(card.dataset.storeId, 0));
      }, { signal });

      this._listEl?.addEventListener('mouseout', () => this._setHoveredPin(null), { signal });

      this._detailEl?.addEventListener('click', (event) => {
        if (event.target.closest(StoreLocator.SELECTORS.detailClose)) this._closeDetail();
      }, { signal });
    }

    _initMap() {
      if (!window.L || !this._mapEl) return;
      this._map = window.L.map(this._mapEl, { scrollWheelZoom: false, zoomControl: false, preferCanvas: true });
      window.L.control.zoom({ position: StoreLocator.MAP.zoomControlPosition }).addTo(this._map);
      window.L.tileLayer(StoreLocator.MAP.tileUrl, {
        attribution: StoreLocator.MAP.attribution,
        maxZoom: StoreLocator.MAP.maxZoom
      }).addTo(this._map);
      this._originLayer = window.L.layerGroup().addTo(this._map);
      this._pinLayer = window.L.layerGroup().addTo(this._map);
      this._map.setView(StoreLocator.MAP.fallbackCenter, StoreLocator.MAP.fallbackZoom);
      this._resizeObserver = new ResizeObserver(() => this._map?.invalidateSize());
      this._resizeObserver.observe(this._mapEl);
    }

    /* ── State changes ── */

    _setOrigin(origin) {
      this._state.origin = origin;
      this._state.selectedId = null;
      this._renderOrigin();
      this._render();
    }

    _clearOrigin() {
      this._setOrigin(null);
    }

    _applyProductFilter() {
      this._state.product = this._productSelect.value;
      this._state.selectedId = null;
      this._render();
    }

    _select(id) {
      this._state.selectedId = id;
      this._render();
      const store = this._storeById(id);
      if (store && this._map && store.lat !== null) {
        this._map.setView([store.lat, store.lng], Math.max(this._map.getZoom(), StoreLocator.MAP.selectZoom));
      }
    }

    _closeDetail() {
      this._state.selectedId = null;
      this._render();
    }

    _locate() {
      if (this._state.locating || !navigator.geolocation) return;
      this._state.locating = true;
      this._geoError.hidden = true;
      this._locateBtn.classList.add(StoreLocator.CLASSES.locateBusy);
      this._locateLabel.textContent = this._strings.locating;
      navigator.geolocation.getCurrentPosition(
        (position) => this._onLocated(position),
        () => this._onLocateError(),
        StoreLocator.GEO_OPTIONS
      );
    }

    _onLocated(position) {
      this._state.locating = false;
      this._locateBtn.classList.remove(StoreLocator.CLASSES.locateBusy);
      this._locateLabel.textContent = this._strings.nearMe;
      this._searchInput.value = '';
      this._closeSuggestions();
      this._setOrigin({
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        radiusKm: StoreLocator.RADIUS.defaultKm,
        label: '',
        source: StoreLocator.ORIGIN_SOURCES.geo
      });
    }

    _onLocateError() {
      this._state.locating = false;
      this._locateBtn.classList.remove(StoreLocator.CLASSES.locateBusy);
      this._locateLabel.textContent = this._strings.nearMe;
      this._geoError.hidden = false;
    }

    /* ── Search & suggestions ── */

    _onSearchInput() {
      clearTimeout(this._suggestTimer);
      const query = this._searchInput.value.trim();
      if (query.length < StoreLocator.GEOCODER.minChars) {
        this._closeSuggestions();
        if (!query && this._state.origin?.source === StoreLocator.ORIGIN_SOURCES.place) this._clearOrigin();
        return;
      }
      this._suggestTimer = setTimeout(() => this._suggest(query), StoreLocator.GEOCODER.debounceMs);
    }

    _onSearchKeydown(event) {
      const isOpen = !this._suggestionsEl.hidden;
      if (event.key === 'ArrowDown' && isOpen) {
        event.preventDefault();
        this._moveActiveSuggestion(1);
      } else if (event.key === 'ArrowUp' && isOpen) {
        event.preventDefault();
        this._moveActiveSuggestion(-1);
      } else if (event.key === 'Escape') {
        this._closeSuggestions();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        this._submitSearch();
      }
    }

    /* Enter / search button: take the highlighted suggestion, else the best one */
    async _submitSearch() {
      clearTimeout(this._suggestTimer);
      const query = this._searchInput.value.trim();
      if (query.length < StoreLocator.GEOCODER.minChars) return;
      if (this._suggestions.length === 0 || this._suggestionsQuery !== query) await this._suggest(query);
      this._pickSuggestion(Math.max(this._state.activeSuggestion, 0));
    }

    async _suggest(query) {
      this._geocodeAbort?.abort();
      this._geocodeAbort = new AbortController();
      const stores = this._matchStores(query);
      let places = [];
      try {
        places = await this._searchPlaces(query, this._geocodeAbort.signal);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.warn('store-locator: address search unavailable', error);
      }
      if (this._searchInput.value.trim() !== query) return;
      this._suggestionsQuery = query;
      this._suggestions = [...stores, ...places];
      this._state.activeSuggestion = -1;
      this._renderSuggestions();
    }

    _matchStores(query) {
      const needle = query.toLowerCase();
      return this._stores
        .filter((store) => store.name.toLowerCase().includes(needle))
        .slice(0, StoreLocator.SUGGEST.storeLimit)
        .map((store) => ({
          kind: StoreLocator.SUGGESTION_KINDS.store,
          id: store.id,
          label: store.name,
          sublabel: this._cityLine(store)
        }));
    }

    /* Typed digits mean an address or postcode; otherwise also ask for cities, which ranks "bordeau" → Bordeaux */
    async _searchPlaces(query, signal) {
      const { placeLimit, cityLimit, cityMinScore, cityType } = StoreLocator.GEOCODER;
      const requests = [this._geocode({ q: query, limit: placeLimit }, signal)];
      if (!/\d/.test(query)) {
        requests.unshift(
          this._geocode({ q: query, limit: cityLimit, type: cityType }, signal)
            .then((features) => features.filter((feature) => feature.properties.score >= cityMinScore))
        );
      }
      const seen = new Set();
      return (await Promise.all(requests))
        .flat()
        .filter((feature) => !seen.has(feature.properties.id) && seen.add(feature.properties.id))
        .map((feature) => this._toPlace(feature));
    }

    async _geocode(params, signal) {
      const search = new URLSearchParams({ ...params, autocomplete: '1' });
      const response = await fetch(`${StoreLocator.GEOCODER.url}?${search}`, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      return Array.isArray(data.features) ? data.features : [];
    }

    _toPlace(feature) {
      const props = feature.properties;
      const [lng, lat] = feature.geometry.coordinates;
      return {
        kind: StoreLocator.SUGGESTION_KINDS.place,
        lat,
        lng,
        radiusKm: this._placeRadiusKm(props),
        label: props.label,
        sublabel: props.context || ''
      };
    }

    _placeRadiusKm(props) {
      const { defaultKm, cityMinKm, cityMaxKm, cityPopulationDivisor } = StoreLocator.RADIUS;
      if (props.type !== StoreLocator.GEOCODER.cityType) return defaultKm;
      const population = this._toFloat(props.population, 0);
      return Math.min(cityMaxKm, Math.max(cityMinKm, Math.sqrt(population) / cityPopulationDivisor));
    }

    _pickSuggestion(index) {
      const suggestion = this._suggestions[index];
      if (!suggestion) return;
      this._searchInput.value = suggestion.label;
      this._closeSuggestions();
      if (suggestion.kind === StoreLocator.SUGGESTION_KINDS.store) {
        if (this._state.origin?.source === StoreLocator.ORIGIN_SOURCES.place) this._clearOrigin();
        this._select(suggestion.id);
        return;
      }
      const { lat, lng, radiusKm, label } = suggestion;
      this._setOrigin({ lat, lng, radiusKm, label, source: StoreLocator.ORIGIN_SOURCES.place });
    }

    _moveActiveSuggestion(step) {
      const count = this._suggestions.length;
      if (count === 0) return;
      this._state.activeSuggestion = (this._state.activeSuggestion + step + count) % count;
      this._renderActiveSuggestion();
    }

    _closeSuggestions() {
      this._suggestionsEl.hidden = true;
      this._searchInput.setAttribute('aria-expanded', 'false');
      this._searchInput.removeAttribute('aria-activedescendant');
      this._state.activeSuggestion = -1;
    }

    /* ── Filtering ── */

    /* With an origin: stores inside its radius, or the nearest few when the radius is empty (`widened`) */
    _visibleStores() {
      const { product, origin } = this._state;
      const list = this._stores.filter((store) => !product || store.products.includes(product));
      if (!origin) {
        const stores = list.sort((a, b) => (a.city || '').localeCompare(b.city || '') || a.name.localeCompare(b.name));
        return { stores, widened: false };
      }
      const byDistance = list
        .filter((store) => store.lat !== null)
        .map((store) => ({ ...store, distanceKm: this._distanceKm(origin, store) }))
        .sort((a, b) => a.distanceKm - b.distanceKm);
      const inRadius = byDistance.filter((store) => store.distanceKm <= origin.radiusKm);
      if (inRadius.length >= StoreLocator.RADIUS.minResults) return { stores: inRadius, widened: false };
      return { stores: byDistance.slice(0, StoreLocator.RADIUS.minResults), widened: true };
    }

    _storeById(id) {
      return this._stores.find((store) => store.id === id) || null;
    }

    /* ── Rendering ── */

    _render() {
      const { stores, widened } = this._visibleStores();
      this._renderCount(stores, widened);
      this._renderList(stores);
      this._renderPins(stores);
      this._renderDetail();
      this._renderMapCard();
      this._fitMap(stores, widened);
    }

    _renderCount(list, widened) {
      const { origin } = this._state;
      const label = list.length === 1 ? this._strings.countOne : this._strings.countOther;
      this._countEl.textContent = `${list.length} ${label}`;
      this._sortedEl.hidden = !origin;
      if (origin) this._sortedEl.textContent = this._strings.withinRadius.replace('__DIST__', this._formatDistance(origin.radiusKm));
      this._noteEl.hidden = !widened;
    }

    _renderSuggestions() {
      const listId = this._suggestionsEl.id;
      this._suggestionsEl.innerHTML = this._suggestions.length === 0
        ? `<li class="store-locator__suggestion-empty">${this._esc(this._strings.suggestEmpty)}</li>`
        : this._suggestions.map((suggestion, index) => `
          <li id="${listId}-${index}" class="store-locator__suggestion" role="option" aria-selected="false" data-suggestion-index="${index}">
            <span class="store-locator__suggestion-label">${this._esc(suggestion.label)}</span>
            ${suggestion.sublabel ? `<span class="store-locator__suggestion-sublabel">${this._esc(suggestion.sublabel)}</span>` : ''}
          </li>`).join('');
      this._suggestionsEl.hidden = false;
      this._searchInput.setAttribute('aria-expanded', 'true');
    }

    _renderActiveSuggestion() {
      const { activeSuggestion } = this._state;
      this._suggestionsEl.querySelectorAll(StoreLocator.SELECTORS.suggestion).forEach((option) => {
        const active = this._toInt(option.dataset.suggestionIndex, -1) === activeSuggestion;
        option.classList.toggle(StoreLocator.CLASSES.suggestionActive, active);
        option.setAttribute('aria-selected', String(active));
        if (active) {
          this._searchInput.setAttribute('aria-activedescendant', option.id);
          option.scrollIntoView({ block: 'nearest' });
        }
      });
    }

    _renderList(list) {
      this._emptyEl.hidden = list.length > 0;
      this._listEl.innerHTML = list.map((store) => this._cardHtml(store)).join('');
    }

    _cardHtml(store) {
      const selected = store.id === this._state.selectedId;
      return `
        <li>
          <button type="button" class="store-locator__card${selected ? ` ${StoreLocator.CLASSES.cardSelected}` : ''}" data-store-id="${store.id}">
            <span class="store-locator__card-body">
              <span class="store-locator__card-meta">
                <span class="store-locator__badge">${this._esc(this._typeBadge(store))}</span>
              </span>
              <span class="store-locator__card-name">${this._esc(store.name)}</span>
              <span class="store-locator__card-address">${this._esc(store.address || '')}</span>
              <span class="store-locator__card-city">${this._esc(this._cityLine(store))}</span>
            </span>
            <span class="store-locator__card-side">
              ${store.distanceKm != null ? `<span class="store-locator__card-distance">${this._formatDistance(store.distanceKm)}</span>` : ''}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m9 6 6 6-6 6"/></svg>
            </span>
          </button>
        </li>`;
    }

    _renderDetail() {
      const store = this._storeById(this._state.selectedId);
      this._detailEl.hidden = !store;
      if (!store) {
        this._detailEl.innerHTML = '';
        return;
      }
      const distance = this._distanceText(store);
      this._detailEl.innerHTML = `
        <div class="store-locator__detail-header">
          <button type="button" class="store-locator__detail-back" data-store-locator-close>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m15 6-6 6 6 6"/></svg>${this._esc(this._strings.backToList)}
          </button>
        </div>
        <div class="store-locator__detail-body">
          <div class="store-locator__card-meta">
            <span class="store-locator__badge">${this._esc(this._typeBadge(store))}</span>
          </div>
          <h2 class="store-locator__detail-title">${this._esc(store.name)}</h2>
          <p class="store-locator__detail-address">${this._esc([store.address, this._cityLine(store)].filter(Boolean).join(' · '))}</p>
          ${distance ? `<p class="store-locator__detail-distance">${this._esc(distance)}</p>` : ''}
          <div class="store-locator__detail-actions">
            <a class="store-locator__action store-locator__action--primary" href="${this._directionsUrl(store)}" target="_blank" rel="noopener">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z"/><circle cx="12" cy="9" r="2.5"/></svg>${this._esc(this._strings.directions)}
            </a>
          </div>
          ${this._productsHtml(store)}
        </div>`;
    }

    _renderMapCard() {
      const store = this._storeById(this._state.selectedId);
      this._mapCardEl.hidden = !store;
      if (!store) {
        this._mapCardEl.innerHTML = '';
        return;
      }
      this._mapCardEl.innerHTML = `
        <div class="store-locator__card-meta">
          <span class="store-locator__badge">${this._esc(this._typeBadge(store))}</span>
        </div>
        <p class="store-locator__map-card-name">${this._esc(store.name)}</p>
        <p class="store-locator__map-card-address">${this._esc([store.address, this._cityLine(store)].filter(Boolean).join(' · '))}</p>
        <div class="store-locator__map-card-actions">
          <a class="store-locator__action store-locator__action--primary" href="${this._directionsUrl(store)}" target="_blank" rel="noopener">${this._esc(this._strings.directions)}</a>
        </div>`;
    }

    _productsHtml(store) {
      if (store.products.length === 0) return '';
      const rows = store.products.map((item) => `<div class="store-locator__product-row">${this._esc(item)}</div>`).join('');
      return `<div class="store-locator__detail-block"><p class="store-locator__detail-block-title">${this._esc(this._strings.productsTitle)}</p>${rows}</div>`;
    }

    /* ── Map pins ── */

    _renderPins(list) {
      if (!this._map) return;
      this._pinLayer.clearLayers();
      this._markers.clear();
      list.filter((store) => store.lat !== null).forEach((store) => {
        const marker = window.L.circleMarker([store.lat, store.lng], this._pinStyle(store.id === this._state.selectedId, false));
        marker.on('click', () => this._select(store.id));
        marker.bindTooltip(store.name);
        marker.addTo(this._pinLayer);
        this._markers.set(store.id, marker);
      });
    }

    _renderOrigin() {
      const { origin } = this._state;
      this._locatedBadge.hidden = origin?.source !== StoreLocator.ORIGIN_SOURCES.geo;
      if (!this._map) return;
      this._originLayer.clearLayers();
      if (!origin) return;
      const { radius, color, stroke, weight, fillOpacity } = StoreLocator.USER_PIN;
      window.L.circle([origin.lat, origin.lng], { ...StoreLocator.RADIUS_CIRCLE, radius: origin.radiusKm * 1000 }).addTo(this._originLayer);
      window.L.circleMarker([origin.lat, origin.lng], { radius, fillColor: color, color: stroke, weight, fillOpacity }).addTo(this._originLayer);
    }

    _setHoveredPin(id) {
      this._markers.forEach((marker, markerId) => {
        if (markerId === this._state.selectedId) return;
        marker.setStyle(this._pinStyle(false, markerId === id));
      });
    }

    _pinStyle(selected, hovered) {
      const pin = StoreLocator.PIN;
      return {
        radius: selected ? pin.selectedRadius : hovered ? pin.hoverRadius : pin.radius,
        fillColor: selected ? pin.selectedColor : hovered ? pin.hoverColor : pin.color,
        color: selected ? pin.selectedStroke : pin.stroke,
        weight: pin.weight,
        fillOpacity: pin.fillOpacity
      };
    }

    /* With an origin, frame its radius circle (plus the nearest stores when it had to widen) */
    _fitMap(list, widened) {
      if (!this._map || this._state.selectedId) return;
      const { origin } = this._state;
      const points = origin && !widened ? [] : list.filter((store) => store.lat !== null).map((store) => [store.lat, store.lng]);
      const bounds = origin
        ? window.L.latLng(origin.lat, origin.lng).toBounds(origin.radiusKm * 2000)
        : window.L.latLngBounds([]);
      points.forEach((point) => bounds.extend(point));
      if (!bounds.isValid()) return;
      this._map.fitBounds(bounds, { padding: StoreLocator.MAP.fitPadding, maxZoom: StoreLocator.MAP.selectZoom });
    }

    /* ── Helpers ── */

    _typeBadge(store) {
      return this._strings.badges[store.type] || store.type;
    }

    _distanceText(store) {
      const { origin } = this._state;
      if (!origin || store.lat === null) return '';
      const template = origin.source === StoreLocator.ORIGIN_SOURCES.geo ? this._strings.distanceFromYou : this._strings.distanceFromPlace;
      return template
        .replace('__DIST__', this._formatDistance(this._distanceKm(origin, store)))
        .replace('__PLACE__', origin.label);
    }

    _cityLine(store) {
      return [store.postalCode, store.city].filter(Boolean).join(' ');
    }

    _directionsUrl(store) {
      const destination = store.lat !== null
        ? `${store.lat},${store.lng}`
        : [store.address, store.postalCode, store.city, store.country].filter(Boolean).join(', ');
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    }

    _distanceKm(from, to) {
      const toRad = (deg) => (deg * Math.PI) / 180;
      const dLat = toRad(to.lat - from.lat);
      const dLng = toRad(to.lng - from.lng);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.sin(dLng / 2) ** 2;
      return 2 * StoreLocator.EARTH_RADIUS_KM * Math.asin(Math.sqrt(a));
    }

    _formatDistance(km) {
      if (km == null) return '';
      if (km < 1) return `${Math.round(km * 1000)} m`;
      const lang = document.documentElement.lang || 'fr';
      return `${km.toLocaleString(lang, { maximumFractionDigits: 1 })} km`;
    }

    _esc(value) {
      return String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
    }

    _toInt(value, fallback) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? fallback : parsed;
    }

    _toFloat(value, fallback) {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? fallback : parsed;
    }
  }

  window.customElements.define('store-locator', StoreLocator);
}
